import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const METAL_BG =
  "linear-gradient(135deg,#FFFFFF 0%,#E4E6EA 22%,#A6ABB4 55%,#F1F2F4 78%,#898F99 100%)";
const CARD_BG = "#101114";
const CARD_BORDER = "#22252A";
const CARD_BORDER_STRONG = "#31353C";

export default function AdminApplications() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("applications"); // applications | requests
  const [apps, setApps] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [updatingId, setUpdatingId] = useState(null);
  const [notice, setNotice] = useState(null); // { kind: 'ok'|'warn'|'err', text }

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [a, r] = await Promise.all([
        api.get("/applications"),
        api.get("/service-requests"),
      ]);
      setApps(a.data);
      setReqs(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === null) return;
    if (user === false || user.role !== "admin") { navigate("/"); return; }
    load();
  }, [user, load, navigate]);

  const setAppStatus = async (id, status) => {
    setUpdatingId(id); setNotice(null);
    try {
      const { data } = await api.patch(`/applications/${id}`, { status });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      const app = apps.find((a) => a.id === id);
      const who = app ? (app.name || app.email) : "the applicant";
      if (status === "approved") {
        if (data.invite_sent)         setNotice({ kind: "ok",   text: `Approved. Invite email sent to ${who}.` });
        else if (data.account_exists) setNotice({ kind: "warn", text: `Approved. ${who} already has an account — no invite needed.` });
        else                          setNotice({ kind: "err",  text: `Approved, but invite email failed: ${data.invite_error || "unknown error"}. Try Resend invite.` });
      } else if (status === "rejected") setNotice({ kind: "warn", text: `Rejected. No email is sent to ${who}.` });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setUpdatingId(null); }
  };

  const resendInvite = async (id) => {
    setUpdatingId(id); setNotice(null);
    try {
      const { data } = await api.post(`/applications/${id}/resend-invite`);
      const app = apps.find((a) => a.id === id);
      const who = app ? (app.name || app.email) : "the applicant";
      if (data.invite_sent)         setNotice({ kind: "ok",   text: `Invite re-sent to ${who}.` });
      else if (data.account_exists) setNotice({ kind: "warn", text: `${who} already has an account — no invite needed.` });
      else                          setNotice({ kind: "err",  text: `Invite failed: ${data.invite_error || "unknown error"}.` });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setUpdatingId(null); }
  };

  const [replyFor, setReplyFor] = useState(null); // request object being replied to
  const [replyDraft, setReplyDraft] = useState({ message: "", confirmed_venue: "", confirmed_price: "", confirmed_datetime: "" });
  const [replySubmitting, setReplySubmitting] = useState(false);

  const openReply = (r) => {
    setReplyDraft({
      message: r.concierge_reply?.message || "",
      confirmed_venue: r.concierge_reply?.confirmed_venue || "",
      confirmed_price: r.concierge_reply?.confirmed_price || "",
      confirmed_datetime: r.concierge_reply?.confirmed_datetime || "",
    });
    setReplyFor(r);
  };

  const submitReply = async () => {
    if (!replyFor || !replyDraft.message.trim()) return;
    setReplySubmitting(true); setNotice(null);
    try {
      const { data } = await api.post(`/service-requests/${replyFor.id}/reply`, replyDraft);
      const reply = {
        message: replyDraft.message.trim(),
        confirmed_venue: replyDraft.confirmed_venue || null,
        confirmed_price: replyDraft.confirmed_price || null,
        confirmed_datetime: replyDraft.confirmed_datetime || null,
        replied_at: new Date().toISOString(),
        replied_by: user?.email,
      };
      setReqs((prev) => prev.map((x) => x.id === replyFor.id ? { ...x, concierge_reply: reply, status: x.status === "pending" ? "in_progress" : x.status } : x));
      setReplyFor(null);
      if (data.email_sent) setNotice({ kind: "ok",  text: `Reply sent — the member was emailed at ${replyFor.user_email}.` });
      else                 setNotice({ kind: "err", text: `Reply saved, but the email failed: ${data.email_error || "unknown"}.` });
    } catch (e) {
      setNotice({ kind: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setReplySubmitting(false); }
  };

  const setReqStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await api.patch(`/service-requests/${id}`, { status });
      setReqs((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setUpdatingId(null); }
  };

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-xs uppercase tracking-[0.3em] text-neutral-400" style={{ background: "radial-gradient(ellipse at 50% 0%, #2a2a2a 0%, #141414 40%, #050505 80%, #000000 100%)" }}>Loading…</div>;
  }

  const rows = tab === "applications" ? apps : reqs;
  const statuses = tab === "applications"
    ? ["all", "pending", "approved", "rejected"]
    : ["all", "pending", "in_progress", "completed", "cancelled"];
  const filtered = filter === "all" ? rows : rows.filter((x) => x.status === filter);
  const counts = statuses.reduce((acc, s) => {
    acc[s] = s === "all" ? rows.length : rows.filter((r) => r.status === s).length;
    return acc;
  }, {});

  return (
    <div
      className="min-h-screen text-neutral-200"
      data-testid="admin-page"
      style={{
        background: "radial-gradient(ellipse at 50% -10%, #202226 0%, #0e0f12 45%, #050506 85%, #000000 100%)",
        fontFamily: "'Archivo', system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      <header
        className="sticky top-0 z-10 backdrop-blur-md"
        style={{ borderBottom: "1px solid #22252A", background: "rgba(0,0,0,0.72)" }}
      >
        <div className="mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div
                aria-hidden
                className="w-40 h-4"
                style={{
                  background: METAL_BG,
                  WebkitMaskImage: "url(/wellnet-logo.png)", maskImage: "url(/wellnet-logo.png)",
                  WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                  WebkitMaskSize: "contain", maskSize: "contain",
                  WebkitMaskPosition: "left center", maskPosition: "left center",
                }}
              />
            </Link>
            <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Admin</span>
          </div>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em]">
            <span className="text-neutral-400 hidden sm:inline">{user?.email}</span>
            <button
              data-testid="admin-logout-btn"
              onClick={async () => { await logout(); navigate("/"); }}
              className="border border-neutral-600 text-neutral-300 px-3 py-1.5 hover:bg-neutral-200 hover:text-black transition-colors"
            >Sign Out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-wrap gap-6 mb-8 text-sm uppercase tracking-[0.2em]" style={{ borderBottom: "1px solid " + CARD_BORDER }}>
          {[
            { id: "applications", label: `Applications (${apps.length})` },
            { id: "requests", label: `Service Requests (${reqs.length})` },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => { setTab(t.id); setFilter("all"); }}
              className={`pb-3 -mb-px border-b-2 transition-colors ${tab === t.id ? "border-neutral-100 text-neutral-100 font-semibold" : "border-transparent text-neutral-500 hover:text-neutral-200"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-8 text-[10px] uppercase tracking-[0.25em]">
          {statuses.map((f) => {
            const on = filter === f;
            return (
              <button
                key={f}
                data-testid={`admin-filter-${f}`}
                onClick={() => setFilter(f)}
                className="px-3 py-1.5 transition-colors"
                style={{
                  background: on ? "#F3F4F6" : "transparent",
                  color: on ? "#0A0B0D" : "#A6ABB4",
                  border: "1px solid " + (on ? "#F3F4F6" : CARD_BORDER_STRONG),
                }}
              >
                {f.replace("_", " ")} <span className="opacity-60">({counts[f]})</span>
              </button>
            );
          })}
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="ml-auto px-3 py-1.5 hover:text-neutral-100 transition-colors"
            style={{ border: "1px solid " + CARD_BORDER_STRONG, color: "#A6ABB4" }}
          >
            Refresh
          </button>
        </div>

        {notice && (
          <div
            data-testid="admin-notice"
            className="text-xs uppercase tracking-[0.2em] px-3 py-2 mb-6 rounded"
            style={{
              color: notice.kind === "ok" ? "#7FC79A" : notice.kind === "err" ? "#F1928A" : "#E6C687",
              border: `1px solid ${notice.kind === "ok" ? "#7FC79A" : notice.kind === "err" ? "#F1928A" : "#E6C687"}`,
              background: "#101114",
            }}
          >
            {notice.text}
          </div>
        )}
        {error && (
          <div
            className="text-xs uppercase tracking-[0.2em] px-3 py-2 mb-6 rounded"
            style={{ color: "#F1928A", border: "1px solid #F1928A", background: "#101114" }}
          >
            {error}
          </div>
        )}
        {loading ? (
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="admin-empty"
            className="p-10 text-center text-neutral-500 text-sm rounded-lg"
            style={{ background: CARD_BG, border: "1px solid " + CARD_BORDER }}
          >
            No {tab === "applications" ? "applications" : "requests"} {filter !== "all" ? `with status "${filter.replace("_", " ")}"` : "yet"}.
          </div>
        ) : tab === "applications" ? (
          <div className="rounded-lg overflow-hidden" style={{ background: CARD_BG, border: "1px solid " + CARD_BORDER }}>
            {filtered.map((a, idx) => (
              <article
                key={a.id}
                data-testid={`admin-app-${a.id}`}
                className="p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
                style={{ borderTop: idx === 0 ? "none" : "1px solid " + CARD_BORDER }}
              >
                <div className="md:col-span-3">
                  <div className="font-black text-lg uppercase tracking-tight text-neutral-100">{a.name}</div>
                  <a href={`mailto:${a.email}`} className="text-sm text-neutral-400 hover:text-neutral-100 break-all">{a.email}</a>
                  {a.phone && <div className="text-sm text-neutral-500 mt-1">{a.phone}</div>}
                </div>
                <div className="md:col-span-6 text-sm whitespace-pre-wrap text-neutral-300">
                  {a.message}
                  {a.referral && (
                    <div className="mt-3 pt-3 text-xs text-neutral-400" style={{ borderTop: "1px solid " + CARD_BORDER }}>
                      <span className="uppercase tracking-[0.2em] text-neutral-500 mr-2">Referral:</span>{a.referral}
                    </div>
                  )}
                </div>
                <div className="md:col-span-3 flex flex-col gap-2 items-start md:items-end text-[10px] uppercase tracking-[0.25em]">
                  <StatusBadge status={a.status} />
                  <div className="text-neutral-500">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</div>
                  <div className="flex gap-2 mt-2 flex-wrap justify-end">
                    <button data-testid={`approve-${a.id}`} disabled={updatingId === a.id || a.status === "approved"} onClick={() => setAppStatus(a.id, "approved")} className="px-3 py-1.5 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-transform hover:-translate-y-px" style={{ background: METAL_BG, color: "#08090B" }}>Approve</button>
                    <button data-testid={`reject-${a.id}`} disabled={updatingId === a.id || a.status === "rejected"} onClick={() => setAppStatus(a.id, "rejected")} className="px-3 py-1.5 disabled:opacity-40 hover:text-neutral-100 hover:border-neutral-200" style={{ border: "1px solid " + CARD_BORDER_STRONG, color: "#A6ABB4" }}>Reject</button>
                    {a.status === "approved" && (
                      <button data-testid={`resend-${a.id}`} disabled={updatingId === a.id} onClick={() => resendInvite(a.id)} className="px-3 py-1.5 disabled:opacity-40 hover:text-neutral-100 hover:border-neutral-200" style={{ border: "1px solid " + CARD_BORDER_STRONG, color: "#A6ABB4" }}>Resend invite</button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-lg overflow-hidden" style={{ background: CARD_BG, border: "1px solid " + CARD_BORDER }}>
            {filtered.map((r, idx) => (
              <article
                key={r.id}
                data-testid={`admin-req-${r.id}`}
                className="p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
                style={{ borderTop: idx === 0 ? "none" : "1px solid " + CARD_BORDER }}
              >
                <div className="md:col-span-3">
                  <div className="font-black text-lg uppercase tracking-tight text-neutral-100">{r.city}</div>
                  <div className="text-sm text-neutral-300 mt-1">{r.user_name || r.user_email}</div>
                  <a href={`mailto:${r.user_email}`} className="text-xs text-neutral-500 hover:text-neutral-100 break-all">{r.user_email}</a>
                </div>
                <div className="md:col-span-6 text-sm text-neutral-300">
                  <div className="font-semibold uppercase tracking-[0.15em] text-xs mb-2 text-neutral-200">{r.services.join(" · ")}</div>
                  {r.preferred_date && <div className="text-sm text-neutral-400">Preferred: {r.preferred_date}</div>}
                  {r.location_detail && <div className="text-sm text-neutral-400">Area: {r.location_detail}</div>}
                  {r.notes && <div className="mt-2 text-sm text-neutral-400 whitespace-pre-wrap">{r.notes}</div>}
                  {r.concierge_reply && (
                    <div
                      className="mt-3 p-3 rounded"
                      style={{ background: "rgba(199,202,209,0.04)", border: "1px solid " + CARD_BORDER_STRONG, boxShadow: "inset 2px 0 0 #C7CAD1" }}
                    >
                      <div className="uppercase tracking-[0.2em] text-[10px] text-[#C7CAD1] mb-1 font-bold">Concierge Reply</div>
                      {r.concierge_reply.confirmed_venue && <div className="text-sm text-neutral-200"><b className="text-neutral-500 font-normal uppercase text-[10px] tracking-[0.2em] mr-2">Venue</b>{r.concierge_reply.confirmed_venue}</div>}
                      {r.concierge_reply.confirmed_price && <div className="text-sm text-neutral-200"><b className="text-neutral-500 font-normal uppercase text-[10px] tracking-[0.2em] mr-2">Price</b>{r.concierge_reply.confirmed_price}</div>}
                      {r.concierge_reply.confirmed_datetime && <div className="text-sm text-neutral-200"><b className="text-neutral-500 font-normal uppercase text-[10px] tracking-[0.2em] mr-2">Time</b>{r.concierge_reply.confirmed_datetime}</div>}
                      <div className="mt-1 whitespace-pre-wrap text-sm text-neutral-400">{r.concierge_reply.message}</div>
                    </div>
                  )}
                </div>
                <div className="md:col-span-3 flex flex-col gap-2 items-start md:items-end text-[10px] uppercase tracking-[0.25em]">
                  <StatusBadge status={r.status} />
                  <div className="text-neutral-500">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</div>
                  <div className="flex flex-wrap gap-2 mt-2 justify-end">
                    <button data-testid={`req-reply-${r.id}`} onClick={() => openReply(r)} className="px-3 py-1.5 font-bold transition-transform hover:-translate-y-px" style={{ background: METAL_BG, color: "#08090B" }}>{r.concierge_reply ? "Edit reply" : "Reply"}</button>
                    <button data-testid={`req-progress-${r.id}`} disabled={updatingId === r.id || r.status === "in_progress"} onClick={() => setReqStatus(r.id, "in_progress")} className="px-3 py-1.5 disabled:opacity-40 hover:text-neutral-100 hover:border-neutral-200" style={{ border: "1px solid " + CARD_BORDER_STRONG, color: "#A6ABB4" }}>In progress</button>
                    <button data-testid={`req-complete-${r.id}`} disabled={updatingId === r.id || r.status === "completed"} onClick={() => setReqStatus(r.id, "completed")} className="px-3 py-1.5 font-bold disabled:opacity-40 transition-transform hover:-translate-y-px" style={{ background: METAL_BG, color: "#08090B" }}>Complete</button>
                    <button data-testid={`req-cancel-${r.id}`} disabled={updatingId === r.id || r.status === "cancelled"} onClick={() => setReqStatus(r.id, "cancelled")} className="px-3 py-1.5 disabled:opacity-40 hover:text-neutral-300" style={{ border: "1px solid " + CARD_BORDER, color: "#6F737B" }}>Cancel</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {replyFor && (
        <div data-testid="reply-modal" className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-md" style={{ background: "rgba(0,0,0,0.72)" }} onClick={(e) => { if (e.target === e.currentTarget) setReplyFor(null); }}>
          <div
            className="w-full max-w-lg p-8 rounded-xl text-neutral-200"
            style={{
              background: "radial-gradient(ellipse at 50% -10%, #202226 0%, #0e0f12 55%, #050506 100%)",
              border: "1px solid " + CARD_BORDER,
              boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="uppercase tracking-[0.3em] text-[10px] text-neutral-500">Concierge Reply</div>
            <h3
              className="text-2xl uppercase tracking-[0.15em] mt-2 mb-1"
              style={{
                fontWeight: 500,
                background: METAL_BG,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {replyFor.city}
            </h3>
            <div className="text-sm text-neutral-400 mb-6">To: {replyFor.user_name || replyFor.user_email} · <span className="text-neutral-500">{replyFor.reference}</span></div>
            <div className="space-y-4">
              <ReplyField label="Confirmed venue">
                <input data-testid="reply-venue" value={replyDraft.confirmed_venue} onChange={(e) => setReplyDraft((d) => ({ ...d, confirmed_venue: e.target.value }))} placeholder="Easton Gym Co. — 8474 Melrose Ave" className="admin-input" />
              </ReplyField>
              <div className="grid grid-cols-2 gap-4">
                <ReplyField label="Price">
                  <input data-testid="reply-price" value={replyDraft.confirmed_price} onChange={(e) => setReplyDraft((d) => ({ ...d, confirmed_price: e.target.value }))} placeholder="$89 / week" className="admin-input" />
                </ReplyField>
                <ReplyField label="Date / time">
                  <input data-testid="reply-datetime" value={replyDraft.confirmed_datetime} onChange={(e) => setReplyDraft((d) => ({ ...d, confirmed_datetime: e.target.value }))} placeholder="Mon 4 Aug, 8am–10pm" className="admin-input" />
                </ReplyField>
              </div>
              <ReplyField label="Message">
                <textarea data-testid="reply-message" rows={5} value={replyDraft.message} onChange={(e) => setReplyDraft((d) => ({ ...d, message: e.target.value }))} placeholder="You're booked. Access is on the app they'll send after check-in. Reply here if anything shifts." className="admin-input resize-y" />
              </ReplyField>
            </div>
            <div className="flex justify-end gap-2 mt-8">
              <button data-testid="reply-cancel" onClick={() => setReplyFor(null)} className="px-4 py-2 hover:text-neutral-100" style={{ border: "1px solid " + CARD_BORDER_STRONG, color: "#A6ABB4" }}>Cancel</button>
              <button data-testid="reply-submit" disabled={replySubmitting || !replyDraft.message.trim()} onClick={submitReply} className="px-4 py-2 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-transform hover:-translate-y-px" style={{ background: METAL_BG, color: "#08090B" }}>
                {replySubmitting ? "Sending…" : "Send reply"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-input {
          width: 100%;
          background: #0B0C0F;
          border: 1px solid ${CARD_BORDER};
          color: #F3F4F6;
          border-radius: 8px;
          padding: 9px 11px;
          color-scheme: dark;
          font: inherit;
          transition: border-color .12s;
        }
        .admin-input:hover { border-color: ${CARD_BORDER_STRONG}; }
        .admin-input:focus { outline: none; border-color: #C7CAD1; }
        .admin-input::placeholder { color: #6F737B; }
      `}</style>
    </div>
  );
}

function ReplyField({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="uppercase tracking-[0.2em] text-[10px] text-neutral-500 font-semibold">{label}</span>
      {children}
    </label>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending:     { color: "#C7CAD1", border: "#31353C", bg: "transparent" },
    approved:    { color: "#08090B", border: "transparent", bg: METAL_BG },
    rejected:    { color: "#6F737B", border: "#31353C", bg: "transparent", strike: true },
    in_progress: { color: "#F3F4F6", border: "#C7CAD1", bg: "transparent" },
    completed:   { color: "#08090B", border: "transparent", bg: METAL_BG },
    cancelled:   { color: "#6F737B", border: "#31353C", bg: "transparent", strike: true },
  };
  const s = styles[status] ?? styles.pending;
  return (
    <span
      className="px-2 py-1 inline-block"
      style={{
        color: s.color,
        border: `1px solid ${s.border}`,
        background: s.bg,
        textDecoration: s.strike ? "line-through" : "none",
      }}
    >
      {(status || "pending").replace("_", " ")}
    </span>
  );
}
